// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { AdminSidebar } from './AdminSidebar';
import { AdminDashboard } from './AdminDashboard';
import { AdminUsers } from './AdminUsers';
import { AdminTopics } from './AdminTopics';
import { AdminSubscriptions } from './AdminSubscriptions';
import { AdminNotifications } from './AdminNotifications';
import { AdminContacts } from './AdminContacts';
import { AdminNews } from './AdminNews';
import { AdminSettings } from './AdminSettings';
import { AdminMediaLibrary } from './AdminMediaLibrary';
import { AdminAnalytics } from './AdminAnalytics';
import { AdminKeyboardShortcuts } from './AdminKeyboardShortcuts';
import { AdminLegalRadar } from './AdminLegalRadar';
import { AdminEmailTemplates } from './AdminEmailTemplates';
import { AdminBrandingSettings } from './AdminBrandingSettings';
import type { AdminLayoutProps, AdminPage } from '../../types/admin';
import { toast } from 'sonner';

export const AdminLayout: React.FC<AdminLayoutProps> = ({ session, profile, onNavigate, onShowAuth }) => {
  const [activePage, setActivePage] = useState<AdminPage | 'media' | 'analytics' | 'branding'>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  const [showCreateForm, setShowCreateForm] = useState(false);

  // Reset showCreateForm when page changes
  useEffect(() => {
    setShowCreateForm(false);
  }, [activePage]);

  // Keyboard shortcuts for admin navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl/Cmd + number keys for quick navigation
      if (modifier && !e.shiftKey && !e.altKey) {
        const shortcuts: Record<string, AdminPage | 'media' | 'analytics'> = {
          '1': 'dashboard',
          '2': 'users',
          '3': 'topics',
          '4': 'subscriptions',
          '5': 'notifications',
          '6': 'contacts',
          '7': 'news',
          '8': 'media',
          '9': 'analytics',
          '0': 'settings',
        };

        if (shortcuts[e.key]) {
          e.preventDefault();
          setActivePage(shortcuts[e.key]);
          toast.info(`Navigated to ${shortcuts[e.key] === 'media' ? 'Media Library' : shortcuts[e.key] === 'analytics' ? 'Analytics' : shortcuts[e.key]}`);
        }
      }

      // Ctrl/Cmd + M to toggle sidebar
      if (modifier && e.key === 'm') {
        e.preventDefault();
        setSidebarOpen(!sidebarOpen);
      }

      // Escape to close sidebar on mobile
      if (e.key === 'Escape' && sidebarOpen && window.innerWidth < 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen]);

  // Check admin access - wait for profile to load
  useEffect(() => {
    // If no session, stop checking (will show login prompt)
    if (!session) {
      console.log('[AdminLayout] No session');
      setCheckingAccess(false);
      return;
    }

    // Wait a bit for profile to load if it's null
    if (!profile) {
      console.log('[AdminLayout] Profile not loaded yet, waiting...');
      const timer = setTimeout(() => {
        setCheckingAccess(false);
        console.log('[AdminLayout] Profile check timeout - profile:', profile ? 'loaded' : 'still null');
      }, 2000);
      return () => clearTimeout(timer);
    }

    // Profile loaded, check role
    setCheckingAccess(false);
    console.log('[AdminLayout] Profile loaded, role:', profile.role);

    // Don't redirect here - let the component render the access denied message
    // This allows users to see what their role is and get instructions
  }, [session, profile]);

  // Show loading state while checking access
  if (checkingAccess) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-luna-bg-light dark:bg-luna-bg-dark">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luna-secondary mx-auto mb-4"></div>
          <p className="text-luna-text-light dark:text-luna-text-dark">Checking admin access...</p>
        </div>
      </div>
    );
  }

  // Access denied component
  if (!session) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-luna-bg-light dark:bg-luna-bg-dark">
        <div className="text-center p-8">
          <span className="material-symbols-outlined text-6xl text-red-500 mb-4">
            block
          </span>
          <h1 className="text-2xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
            Access Denied
          </h1>
          <p className="text-luna-text-light/70 dark:text-luna-text-dark/70 mb-4">
            You must be logged in to access the admin panel.
          </p>
          <button
            onClick={() => {
              console.log('[AdminLayout] Go to Login clicked');
              // Store that we want to return to admin after login
              sessionStorage.setItem('returnToAdmin', 'true');
              console.log('[AdminLayout] returnToAdmin set in sessionStorage');
              // Show auth page directly instead of navigating to Landing
              if (onShowAuth) {
                console.log('[AdminLayout] Calling onShowAuth()');
                onShowAuth();
                console.log('[AdminLayout] onShowAuth() called');
              } else {
                console.warn('[AdminLayout] onShowAuth not available, falling back to Landing');
                // Fallback: navigate to Landing if onShowAuth not available
                onNavigate?.('Landing');
              }
            }}
            className="px-4 py-2 bg-luna-secondary text-white rounded-lg hover:bg-luna-secondary/90"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!profile || (profile.role !== 'admin' && profile.role !== 'super_admin')) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-luna-bg-light dark:bg-luna-bg-dark">
        <div className="text-center p-8">
          <span className="material-symbols-outlined text-6xl text-red-500 mb-4">
            block
          </span>
          <h1 className="text-2xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
            Access Denied
          </h1>
          <p className="text-luna-text-light/70 dark:text-luna-text-dark/70 mb-4">
            Admin privileges required to access this page.
          </p>
          {profile && (
            <p className="text-sm text-luna-text-light/50 dark:text-luna-text-dark/50 mb-4">
              Your current role: <strong>{profile.role}</strong>
            </p>
          )}
          <div className="space-y-2">
            <button
              onClick={() => onNavigate?.('Dashboard')}
              className="px-4 py-2 bg-luna-secondary text-white rounded-lg hover:bg-luna-secondary/90 block w-full"
            >
              Go to Dashboard
            </button>
            <p className="text-xs text-luna-text-light/50 dark:text-luna-text-dark/50 mt-4">
              To gain admin access, run this SQL in Supabase:
              <br />
              <code className="bg-luna-secondary/10 px-2 py-1 rounded mt-1 inline-block">
                UPDATE public.profiles SET role = 'admin' WHERE id = auth.uid();
              </code>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-luna-bg-light dark:bg-luna-bg-dark overflow-hidden">
      <AdminSidebar
        activePage={activePage}
        onNavigate={setActivePage}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />
      
      <main className="flex-1 overflow-auto">
        {/* Mobile menu button */}
        <div className="lg:hidden p-4 border-b border-luna-secondary/30 sticky top-0 bg-white dark:bg-luna-primary z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-luna-secondary/10 active:bg-luna-secondary/20 text-luna-text-light dark:text-luna-text-dark min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
            aria-label="Open sidebar"
          >
            <span className="material-symbols-outlined text-2xl">menu</span>
          </button>
        </div>

        {/* Page Content */}
        <div className="p-4 md:p-6 lg:p-8">
          {activePage === 'dashboard' && (
            <AdminDashboard 
              session={session} 
              profile={profile} 
              onNavigate={(page) => {
                if (page === 'topics' || page === 'news') {
                  setShowCreateForm(true);
                }
                setActivePage(page as AdminPage | 'media' | 'analytics');
              }} 
            />
          )}
          {activePage === 'users' && (
            <AdminUsers session={session} profile={profile} />
          )}
          {activePage === 'topics' && (
            <AdminTopics 
              session={session} 
              profile={profile} 
              showCreateForm={showCreateForm}
            />
          )}
          {activePage === 'subscriptions' && (
            <AdminSubscriptions session={session} profile={profile} />
          )}
          {activePage === 'notifications' && (
            <AdminNotifications session={session} profile={profile} />
          )}
          {activePage === 'emailTemplates' && (
            <AdminEmailTemplates session={session} profile={profile} />
          )}
          {activePage === 'contacts' && (
            <AdminContacts session={session} profile={profile} />
          )}
          {activePage === 'news' && (
            <AdminNews 
              session={session} 
              profile={profile} 
              showCreateForm={showCreateForm}
            />
          )}
          {activePage === 'legalradar' && (
            <AdminLegalRadar session={session} profile={profile} />
          )}
          {activePage === 'settings' && (
            <AdminSettings session={session} profile={profile} />
          )}
          {(activePage as any) === 'media' && (
            <AdminMediaLibrary session={session} profile={profile} />
          )}
          {(activePage as any) === 'analytics' && (
            <AdminAnalytics session={session} profile={profile} />
          )}
          {(activePage as any) === 'branding' && (
            <AdminBrandingSettings session={session} profile={profile} />
          )}
        </div>
      </main>
      <AdminKeyboardShortcuts />
    </div>
  );
};
